import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar, Clock, ArrowRight, BookOpen } from 'lucide-react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dummy blog posts
  const dummyPosts = [
    {
      id: '1',
      title: 'Memulai Project IoT dengan ESP32: Panduan Lengkap untuk Pemula',
      slug: 'memulai-project-iot-esp32',
      excerpt: 'Tutorial step-by-step membuat project IoT pertama menggunakan ESP32. Dari setup environment hingga mengirim data ke cloud.',
      category: 'IoT',
      reading_time: 8,
      published_at: new Date('2024-01-15')
    },
    {
      id: '2',
      title: 'Web Development untuk Mahasiswa Teknik: Tips Memulai',
      slug: 'web-dev-untuk-mahasiswa-teknik',
      excerpt: 'Panduan praktis belajar web development untuk mahasiswa engineering. Tech stack yang cocok dan project ideas untuk portfolio.',
      category: 'Web Development',
      reading_time: 6,
      published_at: new Date('2024-01-10')
    },
    {
      id: '3',
      title: 'Integrasi MQTT untuk Real-time IoT Monitoring',
      slug: 'integrasi-mqtt-realtime-monitoring',
      excerpt: 'Cara implementasi MQTT broker dan client untuk sistem monitoring real-time. Cocok untuk project greenhouse, smart home, atau industrial monitoring.',
      category: 'IoT',
      reading_time: 10,
      published_at: new Date('2024-01-05')
    },
    {
      id: '4',
      title: 'Deploy React App ke DigitalOcean dengan Docker',
      slug: 'deploy-react-digitalocean-docker',
      excerpt: 'Tutorial deployment React application ke VPS DigitalOcean menggunakan Docker dan Nginx. Production-ready setup dengan SSL certificate.',
      category: 'DevOps',
      reading_time: 12,
      published_at: new Date('2023-12-28')
    },
    {
      id: '5',
      title: 'Desain PCB untuk Pemula: Dari Skematik hingga Gerber Files',
      slug: 'desain-pcb-pemula',
      excerpt: 'Panduan lengkap membuat PCB custom menggunakan KiCAD. Dari schematic design, layout, hingga export Gerber files untuk manufaktur.',
      category: 'Electronics',
      reading_time: 15,
      published_at: new Date('2023-12-20')
    },
    {
      id: '6',
      title: 'MATLAB untuk Control Systems: Tips & Tricks',
      slug: 'matlab-control-systems-tips',
      excerpt: 'Kumpulan tips praktis menggunakan MATLAB untuk simulasi dan analisis control systems. Cocok untuk tugas kuliah dan project penelitian.',
      category: 'Engineering',
      reading_time: 7,
      published_at: new Date('2023-12-15')
    }
  ];

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog`);
      if (response.data && response.data.length > 0) {
        setPosts(response.data);
      } else {
        setPosts(dummyPosts);
      }
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      setPosts(dummyPosts);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    const d = new Date(date);
    return d.toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getCategoryColor = (category) => {
    const colors = {
      'IoT': 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30',
      'Web Development': 'bg-blue-500/10 text-blue-400 border-blue-500/30',
      'DevOps': 'bg-purple-500/10 text-purple-400 border-purple-500/30',
      'Electronics': 'bg-amber-500/10 text-amber-400 border-amber-500/30',
      'Engineering': 'bg-green-500/10 text-green-400 border-green-500/30'
    };
    return colors[category] || 'bg-slate-500/10 text-slate-400 border-slate-500/30';
  };

  return (
    <div className="min-h-screen pt-20" data-testid="blog-page">
      {/* Hero */}
      <section className="pt-32 pb-20 px-6 noise-overlay">
        <div className="container mx-auto text-center">
          <BookOpen size={64} className="text-cyan-400 mx-auto mb-6" />
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="blog-title">
            Blog &
            <br />
            <span className="gradient-text">Learning Resources</span>
          </h1>
          <p className="text-lg text-slate-300 max-w-3xl mx-auto">
            Artikel, tutorial, dan tips seputar web development, IoT, DevOps, dan engineering tools.
          </p>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="pb-20 px-6">
        <div className="container mx-auto max-w-6xl">
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block w-12 h-12 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-slate-400 mt-4">Loading articles...</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-20">
              <BookOpen size={64} className="text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">Belum ada artikel. Coming soon!</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Featured Post (First one) */}
              {posts[0] && (
                <Card className="glass-effect border-slate-700 overflow-hidden card-hover group" data-testid="featured-post">
                  <div className="grid md:grid-cols-2 gap-0">
                    <div className="relative h-64 md:h-auto bg-gradient-to-br from-cyan-500/20 to-blue-600/20">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <BookOpen size={80} className="text-cyan-400/30" />
                      </div>
                      <div className="absolute top-4 left-4">
                        <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getCategoryColor(posts[0].category)}`}>
                          Featured
                        </span>
                      </div>
                    </div>
                    <div className="p-8">
                      <span className={`inline-block px-3 py-1 text-xs font-medium rounded-full border mb-4 ${getCategoryColor(posts[0].category)}`}>
                        {posts[0].category}
                      </span>
                      <h2 className="text-2xl font-bold text-white mb-3 group-hover:text-cyan-400 transition-colors" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                        {posts[0].title}
                      </h2>
                      <p className="text-slate-400 text-sm mb-4">
                        {posts[0].excerpt}
                      </p>
                      <div className="flex items-center space-x-4 text-slate-500 text-xs mb-6">
                        <div className="flex items-center space-x-1">
                          <Calendar size={14} />
                          <span>{formatDate(posts[0].published_at)}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Clock size={14} />
                          <span>{posts[0].reading_time} min read</span>
                        </div>
                      </div>
                      <Button className="btn-secondary group" data-testid="featured-read-btn">
                        Baca Artikel
                        <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </div>
                  </div>
                </Card>
              )}

              {/* Other Posts */}
              <div className="grid md:grid-cols-2 gap-6">
                {posts.slice(1).map((post, index) => (
                  <Card key={post.id || index} className="glass-effect border-slate-700 p-6 card-hover group" data-testid={`blog-post-${index}`}>
                    <span className={`inline-block px-3 py-1 text-xs font-medium rounded-full border mb-4 ${getCategoryColor(post.category)}`}>
                      {post.category}
                    </span>
                    <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-cyan-400 transition-colors" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      {post.title}
                    </h3>
                    <p className="text-slate-400 text-sm mb-4 line-clamp-2">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 text-slate-500 text-xs">
                        <div className="flex items-center space-x-1">
                          <Calendar size={12} />
                          <span>{formatDate(post.published_at)}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Clock size={12} />
                          <span>{post.reading_time} min</span>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" className="text-cyan-400 hover:text-cyan-300 p-0" data-testid={`read-btn-${index}`}>
                        Baca
                        <ArrowRight size={14} className="ml-1 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-20 px-6 bg-slate-900/50">
        <div className="container mx-auto">
          <Card className="glass-effect border-slate-700 p-6 sm:p-12 text-center max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 break-words" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              More Content Coming Soon!
            </h2>
            <p className="text-slate-300 mb-8 break-words">
              Kami akan terus menambahkan artikel, tutorial, dan resources berguna untuk komunitas engineering.
            </p>
            <div className="flex justify-center">
              <Link to="/contact" className="w-full sm:w-auto">
                <Button className="btn-primary text-base px-8 py-6 w-full sm:w-auto" data-testid="blog-contact-cta">
                  Request Topic
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
}